import {
  Box,
  Flex,
  Heading,
  Text,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  MdArrowBack as BackIcon,
} from 'react-icons/md';
import { useAuth } from '@/hooks/useAuth';
import { useLocation } from 'wouter';
import { ROUTES } from '@/types';
import { useHeaderScroll } from '@/hooks/useHeaderScroll';

interface HeaderProps {
  // Left section - title/logo
  title?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  hideWordmark?: boolean;

  // Center section - flexible content slot
  centerContent?: React.ReactNode;

  // Right section - flexible actions slot
  rightContent?: React.ReactNode;
}

export default function Header({
  title = 'BytePantry',
  showBackButton = false,
  onBackClick,
  hideWordmark = false,
  centerContent,
  rightContent,
}: HeaderProps) {
  const [, navigate] = useLocation();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const { isVisible } = useHeaderScroll();
  const { isAuthenticated, user } = useAuth();

  const initials = (() => {
    if (!user) return '';
    const parts = (user.name ?? '').split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return '';
  })();

  return (
    <Box
      as="header"
      bg="bg.surface"
      position="sticky"
      top={0}
      zIndex={10}
      boxShadow="sm"
      overflow="visible"
      transform={isVisible ? 'translateY(0)' : 'translateY(-100%)'}
      transition="transform 0.3s ease-in-out"
    >
      {/* Fills the status bar safe area with the brand colour on edge-to-edge Android/iOS */}
      <Box h="env(safe-area-inset-top, 0px)" bg="accent.emphasis" />
      {/* Main header row */}
      <Box borderBottom="1px" borderColor="border.muted">
        <Flex
          maxW="100%"
          mx="0"
          px={4}
          py={3}
          align="center"
          justify="space-between"
          gap={4}
          w="100%"
        >
          {/* Left side - Back button (mobile only) or Logo */}
          {showBackButton && isMobile ? (
            <Flex align="center" gap={3} minW="0" flex="1" maxW="calc(100% - 60px)">
              <IconButton
                aria-label="Go back"
                size="sm"
                variant="ghost"
                onClick={onBackClick}
                flexShrink={0}
              >
                <BackIcon />
              </IconButton>
              <Text
                fontFamily="heading"
                fontWeight="800"
                fontSize="lg"
                color="text.primary"
                truncate
                minW="0"
              >
                {title}
              </Text>
            </Flex>
          ) : (
            <Flex
              align="center"
              gap={2}
              cursor="pointer"
              onClick={() => navigate(ROUTES.HOME())}
              minW="fit-content"
              flexShrink={0}
            >
              <Box w="28px" h="28px" flexShrink={0} display="flex" alignItems="center" justifyContent="center">
                <img src="/logo.png" alt="Byte Pantry" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
              </Box>
              <Heading
                fontFamily="heading"
                fontWeight="800"
                fontSize={{ base: '15px', md: '17px' }}
                color="text.primary"
                whiteSpace="nowrap"
                maxW={hideWordmark ? '0' : '200px'}
                overflow="hidden"
                opacity={hideWordmark ? 0 : 1}
                transition="max-width 0.2s ease, opacity 0.2s ease"
              >
                Byte <Text as="span" color="accent.emphasis">Pantry</Text>
              </Heading>
            </Flex>
          )}

          {/* Center section - flexible content slot */}
          {centerContent}

          {/* Right section - flexible actions slot */}
          {rightContent}

          {/* User avatar */}
          {isAuthenticated && user && (
            <Box flexShrink={0}>
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name ?? 'User'}
                  style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <Flex
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  bg="accent.secondary"
                  color="white"
                  align="center"
                  justify="center"
                  fontFamily="heading"
                  fontWeight="800"
                  fontSize="12px"
                >
                  {initials}
                </Flex>
              )}
            </Box>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
